/*
Copyright (c) Uber Technologies, Inc.

This source code is licensed under the MIT license found in the
LICENSE file in the root directory of this source tree.
*/
import * as React from 'react';
import { LocaleContext } from '../locale';
// Components
import { StyledList, StyledEmptyState, StyledOptgroupHeader, StyledMenuDivider, VisuallyHiddenStatus } from './styled-components';
import OptionList from './option-list';
import { getOverrides } from '../helpers/overrides';
// Types
import type { RenderItemProps, StatelessMenuProps } from './types';
import type { Locale } from '../locale';
import { isFocusVisible, forkFocus, forkBlur } from '../utils/focusVisible';
import type { SyntheticEvent } from 'react';
export default function Menu(props: StatelessMenuProps) {
  const {
    overrides = {},
    'aria-label': ariaLabel = 'Menu',
    rootRef = React.createRef(),
    focusMenu = () => {},
    unfocusMenu = () => {},
    handleMouseLeave = () => {},
    handleKeyDown = (event: KeyboardEvent) => {},
    renderAll = false,
    rovingFocus = false
  } = props;
  const localeContextState = React.useContext(LocaleContext);
  const [focusVisible, setFocusVisible] = React.useState(false);
  const handleFocus = (event: SyntheticEvent) => {
    if (isFocusVisible(event)) {
      setFocusVisible(true);
    }
  };
  const handleBlur = (event: SyntheticEvent) => {
    if (focusVisible !== false) {
      setFocusVisible(false);
    }
  };
  const [List, listProps] = getOverrides(overrides.List, StyledList);
  const [Option, optionProps] = getOverrides(overrides.Option, OptionList);
  const [EmptyState, emptyStateProps] = getOverrides(overrides.EmptyState, StyledEmptyState);
  const [OptgroupHeader, optgroupHeaderProps] = getOverrides(overrides.OptgroupHeader, StyledOptgroupHeader);
  const [MenuDivider, menuDividerProps] = getOverrides(overrides.MenuDivider, StyledMenuDivider);
  const groupedItems = Array.isArray(props.items) ? {
    __ungrouped: props.items
  } : props.items;
  const optgroups = Object.keys(groupedItems);
  const [elements] = optgroups.reduce(([els, itemIndex], optgroup) => {
    if (optgroup !== '__ungrouped') {
      els.push(
      // @ts-ignore
      <OptgroupHeader key={optgroup} {...optgroupHeaderProps}>
            {optgroup}
          </OptgroupHeader>);
    }

    // @ts-ignore
    const groupItems = groupedItems[optgroup].map((item, index) => {
      itemIndex = itemIndex + 1;
      const {
        getRequiredItemProps = (item, index) => ({} as RenderItemProps)
      } = props;
      if (item.divider === true) {
        return <MenuDivider key={itemIndex} {...menuDividerProps} />;
      }
      const {
        disabled,
        isFocused,
        isHighlighted,
        isKeyboardFocused,
        resetMenu = () => {},
        ...restProps
      } = getRequiredItemProps(item, itemIndex);
      return <Option renderAll={renderAll} key={itemIndex} item={item} overrides={props.overrides} resetMenu={resetMenu} role="option" $disabled={disabled} $isFocused={isFocused} $isHighlighted={isHighlighted} $isKeyboardFocused={isKeyboardFocused} aria-disabled={disabled} aria-selected={isHighlighted && isFocused} {...restProps} {...optionProps} />;
    });
    return [els.concat(groupItems), itemIndex];
  }, [[], -1]);

  // @ts-ignore
  const isEmpty = optgroups.every(optgroup => !groupedItems[optgroup].length);
  const totalNumberOfItems = React.useMemo(() => optgroups.reduce((acc, optgroup) => acc + groupedItems[optgroup].length, 0), [optgroups, groupedItems]);

  // Use content-based announcements
  const [statusMessage, setStatusMessage] = React.useState<React.ReactNode>('');
  React.useEffect(() => {
    const timerId = setTimeout(() => {
      if (isEmpty) {
        // set the status message to the no results message
        setStatusMessage(localeContextState.menu.noResultsStatus);
      } else {
        setStatusMessage(localeContextState.menu.itemsAvailableStatus(totalNumberOfItems));
      }
    }, 100);
    return () => clearTimeout(timerId);
  }, [totalNumberOfItems, isEmpty]);
  return <LocaleContext.Consumer>
      {(locale: Locale) => <>
          <List aria-activedescendant={props.activedescendantId || null} role="listbox" aria-label={ariaLabel} ref={rootRef}
      // rovingFocus: neutralize the menu's focus-on-hover/focus. By default
      // hovering or focusing the list calls focusMenu → rootRef.focus(),
      // which (especially under macOS VoiceOver, where each swipe blurs and
      // the next re-focuses) yanks focus back to the <ul> and re-centers —
      // the scroll-snap bug. In rovingFocus mode focus lives on the options
      // (via the container's roving tabindex), so the list itself must not
      // grab focus. We keep only the focus-visible bookkeeping. isFocused
      // stays true from the consumer's initialState (no unfocusMenu blur).
      onMouseEnter={rovingFocus ? undefined : focusMenu} onMouseLeave={handleMouseLeave} onMouseOver={rovingFocus ? undefined : focusMenu} onFocus={rovingFocus ? handleFocus : forkFocus({
        onFocus: focusMenu
      }, handleFocus)} onBlur={rovingFocus ? handleBlur : forkBlur({
        onBlur: unfocusMenu
      }, handleBlur)}
      // @ts-ignore
      onKeyDown={event => {
        if (props.isFocused) {
          handleKeyDown(event);
        }
      }}
      // rovingFocus: take the <ul> out of the tab order — focus reaches
      // options via the roving tabindex, not the container.
      tabIndex={rovingFocus ? -1 : 0} data-baseweb="menu" $isFocusVisible={focusVisible} {...listProps}>
            {isEmpty ? <EmptyState role="status" aria-live="polite" aria-atomic="true" {...emptyStateProps}>
                {props.noResultsMsg || locale.menu.noResultsMsg}
              </EmptyState> : elements}
          </List>
          <VisuallyHiddenStatus role="status" aria-live="polite" aria-atomic="true">
            {statusMessage}
          </VisuallyHiddenStatus>
        </>}
    </LocaleContext.Consumer>;
}